import { COMPACT_HEADER_STYLE } from '../../lib/gameChrome'
import React from 'react'
import {
  ActivityIndicator,
  Image,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
  type DimensionValue,
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import type { GameConfig, GameResponse } from '@warcarp/api-types'
import { useAuth } from '../../hooks/useAuth'
import { useTheme } from '../../hooks/useTheme'
import { useCreateGame, useGames } from '../../hooks/useGames'
import { apiPost } from '../../lib/api'
import { reportError } from '../../lib/reporting'
import { setItem } from '../../lib/storage'
import { themeVarsDark, themeVarsLight } from '../../lib/themeVars'
import { TUTORIAL_MODE_KEY, PRACTICE_GAME_CLASSIFICATION, isResumablePracticeGame } from '../../lib/tutorial'
import { Button } from '../../components/ui/Button'
import { tutorialImageHeight } from '../../lib/tutorialLayout'
import { useTutorialMilestones } from '../../hooks/useTutorialMilestones'

type TutorialSlide = {
  eyebrow: string
  title: string
  stepNumber?: number
  body: string
  image: number
  nextAction: string
  annotations: Array<{ label: string; top: DimensionValue; left?: DimensionValue; right?: DimensionValue }>
  steps?: string[]
}

const SLIDES: TutorialSlide[] = [
  {
    eyebrow: 'THE BIG PICTURE',
    title: 'Every turn has a rhythm',
    body: 'Build your position, attack neighboring enemies, move armies. Then pass your turn to the next player.',
    image: require('../../assets/tutorial/turn-loop.png'),
    nextAction: 'Place troops',
    annotations: [],
    steps: [
      'PLACE TROOPS',
      'ATTACK',
      'FORTIFY + END TURN',
    ],
  },
  {
    eyebrow: 'REINFORCE',
    title: 'Place troops',
    stepNumber: 1,
    body: 'At the start of each turn, you get troops to place based on the territories and regions you hold. Tap any territory you control to add troops to them, then tap Commit to move to the attack phase.',
    image: require('../../assets/tutorial/place-troops.png'),
    nextAction: 'Attack',
    annotations: [],
    steps: [
      'TAP TO ADD TO YOUR TERRITORIES',
      'COMMIT TO MOVE TO ATTACK PHASE',
    ],
  },
  {
    eyebrow: 'ATTACK',
    title: 'Attack',
    stepNumber: 2,
    body: 'Tap your territory first, then tap an adjacent enemy territory. Choose your dice and roll. You can attack again while the attack phase is open.',
    image: require('../../assets/tutorial/attack-border.png'),
    nextAction: 'Fortify + end turn',
    annotations: [],
    steps: [
      'TAP A TERRITORY TO ATTACK FROM',
      'TAP AN ADJACENT ENEMY TERRITORY',
      'CHOOSE DICE, THEN ATTACK',
    ],
  },
  {
    eyebrow: 'FINISH THE TURN',
    title: 'Fortify + End Turn',
    stepNumber: 3,
    body: 'Fortify by moving armies between connected territories, then tap End Turn.',
    image: require('../../assets/tutorial/fortify-turn.png'),
    nextAction: 'Start practice match',
    annotations: [],
    steps: [
      'MOVE ARMIES',
      'END TURN',
    ],
  },
]

function AnnotatedExample({ slide }: { slide: TutorialSlide }) {
  const { width } = useWindowDimensions()
  const isPhone = width < 600
  const [cardWidth, setCardWidth] = React.useState(Math.min(760, Math.max(0, width - 32)))
  const steps = slide.steps ?? slide.annotations.map((annotation) => annotation.label.replace(/^\d+ · /, ''))

  return (
    <View className="w-full max-w-[760px] rounded-2xl overflow-hidden border-2 border-olive bg-paper shadow-sm"
      style={{ flexGrow: 0, flexShrink: 0 }} onLayout={(event) => setCardWidth(event.nativeEvent.layout.width)}>
      <Image
        source={slide.image}
        accessibilityLabel={`${slide.title} example illustration`}
        resizeMode="cover"
        style={{
          width: '100%',
          height: tutorialImageHeight(cardWidth, isPhone),
          flexShrink: 0,
        }}
      />
      {slide.annotations.map((annotation) => (
        <View
          key={annotation.label}
          className="absolute rounded-lg border-2 border-blood bg-paper px-2.5 py-1.5 shadow-lg"
          style={{
            top: annotation.top,
            ...(annotation.left ? { left: annotation.left } : { right: annotation.right ?? '5%' }),
            maxWidth: '48%',
          }}
        >
          <Text className="font-typed text-xs font-bold text-ink" numberOfLines={2}>{annotation.label}</Text>
        </View>
      ))}
      <View className="flex-row flex-wrap gap-2 border-t-2 border-olive bg-paper px-3 py-3">
        {steps.map((step, index) => (
          <View key={`key-${step}`} className="flex-row items-center gap-1.5 rounded-lg border border-olive bg-canvas px-2.5 py-1.5">
            <View className="h-5 w-5 items-center justify-center rounded-full bg-blood">
              <Text className="font-stencil text-[10px] text-paper">{index + 1}</Text>
            </View>
            <Text className="font-typed text-xs font-bold text-ink">{step}</Text>
          </View>
        ))}
      </View>
    </View>
  )
}

/** Short visual primer. The optional live game is launched from this page. */
export default function TutorialScreen() {
  const scroll = React.useRef<ScrollView>(null)
  const router = useRouter()
  const { user, refreshUser } = useAuth()
  const { theme } = useTheme()
  const createGame = useCreateGame()
  const { mutate: recordMilestone } = useTutorialMilestones()
  React.useEffect(() => { if (user?.id) recordMilestone('started') }, [user?.id, recordMilestone])
  const { data: games, refetch: refetchGames } = useGames()
  const [slideIndex, setSlideIndex] = React.useState(0)
  const [error, setError] = React.useState<string | null>(null)
  const slide = SLIDES[slideIndex]!
  const isLast = slideIndex === SLIDES.length - 1
  const [launching, setLaunching] = React.useState(false)
  const launchInFlight = React.useRef(false)
  const busy = launching || createGame.isPending
  const themeVars = theme === 'dark' ? themeVarsDark : themeVarsLight
  const canvasColor = theme === 'dark' ? '#1a1f12' : '#ece1c2'
  const { width: windowWidth } = useWindowDimensions()
  const isPhoneFooter = windowWidth < 600
  React.useEffect(() => { scroll.current?.scrollTo({ y: 0, animated: false }) }, [slideIndex])

  async function markWelcomeSeen() {
    try {
      await apiPost('/api/v1/auth/me/tutorial-welcome/seen')
      // Keep the in-memory auth state in sync before returning to Home. The
      // server mutation alone is not enough for a Home screen already mounted
      // in the navigation stack; it would still hold the old `false` value.
      await refreshUser()
    } catch (err: unknown) {
      // The intro is still dismissed locally if this idempotent follow-up
      // fails; report it so a transient API problem is visible.
      reportError(err, { tags: { surface: 'tutorial', action: 'mark-welcome-seen' } })
    }
  }

  async function startLiveTutorial() {
    if (launchInFlight.current || busy) return
    launchInFlight.current = true
    setLaunching(true)
    setError(null)
    try {
      // Reuse an unfinished practice match so returning learners resume where
      // they left off. Completed or abandoned matches are intentionally not
      // reused; starting practice again should create a clean board.
      let availableGames = games
      if (!availableGames) {
        const refreshed = await refetchGames()
        availableGames = refreshed.data
      }
      const existingTutorial = availableGames?.find(isResumablePracticeGame)
      if (existingTutorial) {
        await markWelcomeSeen()
        router.replace(`/(app)/games/${existingTutorial.id}` as never)
        return
      }

      const game = await createGame.mutateAsync({
        name: 'WarCarp Tutorial',
        ...PRACTICE_GAME_CLASSIFICATION,
        mapId: 'carpathia',
        mode: 'SOLO',
        minPlayers: 2,
        maxPlayers: 3,
        autoStart: false,
        // This marker only controls the tutorial game's opening order on the
        // server. The game screen itself is the normal, unmodified layout.
        config: {
          startingTroops: 4,
          autoAssignTerritories: true,
          maxAttacksPerTurn: null,
          maxFortifiesPerTurn: 1,
          eliminatePlayerBonus: 5,
          cardBonusSchedule: 'cycle',
          tutorial: true,
        } as Partial<GameConfig> & { tutorial: true },
      })
      // Every practice match launched from onboarding starts with contextual
      // help enabled. Ordinary games remain opt-in from the game menu.
      try {
        await setItem(TUTORIAL_MODE_KEY(game.id), 'true')
      } catch (err: unknown) {
        // Storage failure must not prevent the user from entering the game.
        reportError(err, { tags: { surface: 'tutorial', action: 'enable-inline-help' } })
      }
      await apiPost<GameResponse>(`/api/v1/games/${game.id}/add-bot`, { displayName: 'Hannibal' })
      await apiPost<GameResponse>(`/api/v1/games/${game.id}/add-bot`, { displayName: 'Sun Tzu' })
      await apiPost<GameResponse>(`/api/v1/games/${game.id}/start`, {})
      await markWelcomeSeen()
      // No tutorial query: entering the practice game uses the normal game
      // screen and layout. The tutorial is this visual page.
      router.replace(`/(app)/games/${game.id}` as never)
    } catch (err: unknown) {
      reportError(err, { tags: { surface: 'tutorial', action: 'create' } })
      setError(err instanceof Error ? err.message : 'Could not start the practice game.')
    } finally {
      launchInFlight.current = false
      setLaunching(false)
    }
  }

  async function exitTutorial() {
    await markWelcomeSeen()
    router.replace('/(app)')
  }

  function next() {
    if (isLast) {
      recordMilestone('completed')
      void startLiveTutorial()
    } else {
      setSlideIndex((current) => current + 1)
    }
  }

  return (
    <SafeAreaView
      className={`${theme === 'dark' ? 'dark ' : ''}flex-1 bg-canvas`}
      style={[{ flex: 1, minHeight: '100%', backgroundColor: canvasColor }, themeVars]}
    >
      <View className="flex-row items-center gap-3 border-b border-olive bg-paper-up px-4" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => void exitTutorial()}
          accessibilityRole="button"
          accessibilityLabel="Exit tutorial"
          className="p-1"
        >
          <Text className="font-typed text-base text-blood">← Exit</Text>
        </TouchableOpacity>
        <Text className="flex-1 font-stencil text-lg uppercase text-ink">Learn to Play</Text>
        <Text className="font-stencil text-xs text-ink-soft">{slideIndex + 1}/{SLIDES.length}</Text>
      </View>

      <View className="min-h-0 flex-1">
        <ScrollView ref={scroll} className="flex-1" contentContainerStyle={{ alignItems: 'center', paddingHorizontal: 16, paddingBottom: 24, paddingTop: 20 }} showsVerticalScrollIndicator={false}>
          <AnnotatedExample slide={slide} />

          <View className="w-full max-w-[760px] gap-2 px-1 pt-5">
            <View className="flex-row items-center gap-3">
              {slide.stepNumber && (
                <View className="h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blood">
                  <Text className="font-stencil text-sm text-paper">{slide.stepNumber}</Text>
                </View>
              )}
              <Text className="flex-1 font-stencil text-2xl uppercase text-ink">{slide.title}</Text>
            </View>
            <Text className="font-typed text-base leading-6 text-ink-soft">{slide.body}</Text>
          </View>

          {error && <Text className="w-full max-w-[760px] px-1 pt-4 text-center font-typed text-xs text-blood">{error}</Text>}
        </ScrollView>

        <View className="border-t border-olive bg-canvas px-4 pb-3 pt-5">
          <View className="w-full max-w-[760px] self-center gap-2">
            {/* The skip is a real secondary button beside (desktop) or under
                (phone) the primary action, not a small link: many players
                would rather learn on a live board. Hidden on the last slide,
                where the primary action already starts the practice match. */}
            <View style={isPhoneFooter ? { gap: 8 } : { flexDirection: 'row-reverse', gap: 12 }}>
              <View style={isPhoneFooter ? undefined : { flex: 1 }}>
                <Button
                  title={busy ? 'Preparing Practice Game…' : isLast ? 'Start practice match' : `Next: ${slide.nextAction}`}
                  onPress={next}
                  loading={busy}
                  disabled={busy}
                />
              </View>
              {!busy && !isLast && (
                <View style={isPhoneFooter ? undefined : { flex: 1 }}>
                  <Button
                    testID="tutorial-skip-to-practice"
                    variant="secondary"
                    title="Skip to practice game"
                    textClassName="font-stencil uppercase tracking-wider"
                    accessibilityLabel="Skip to live practice game"
                    icon={<MaterialCommunityIcons name="sword-cross" size={18} color={theme === 'dark' ? '#e0463f' : '#a01818'} />}
                    onPress={() => void startLiveTutorial()}
                  />
                </View>
              )}
            </View>
            {slideIndex > 0 && !busy && (
              <View className="flex-row">
                <TouchableOpacity
                  onPress={() => setSlideIndex((current) => current - 1)}
                  accessibilityRole="button"
                  accessibilityLabel="Previous slide"
                  className="px-2 py-1"
                >
                  <Text className="font-typed text-sm text-ink-soft">← Previous</Text>
                </TouchableOpacity>
              </View>
            )}
            {busy && (
              <View className="flex-row items-center justify-center gap-2 py-2">
                <ActivityIndicator color="#991b1b" />
                <Text className="font-typed text-sm text-ink-soft">Seating two bot commanders…</Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </SafeAreaView>
  )
}
